/**
 * Creates a fetch-based API client that mirrors the axios.create() interface.
 *
 * @param {string} baseURL - The base URL to prepend to all request paths.
 * @param {Object} defaultHeaders - Headers to include with every request.
 * @returns {{ get: Function, post: Function, put: Function, delete: Function }}
 */
const createApiClient = (baseURL, defaultHeaders) => {
  const request = async (path, { method, body, signal } = {}) => {
    const headers = { ...defaultHeaders };

    if (body !== undefined && !(body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const url = `${baseURL.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
    const response = await fetch(url, {
      method,
      headers,
      body:
        body instanceof FormData
          ? body
          : body !== undefined
          ? JSON.stringify(body)
          : undefined,
      signal,
    });

    if (!response.ok) {
      const error = new Error(
        `HTTP ${response.status}: ${response.statusText}`
      );
      error.response = {
        status: response.status,
        statusText: response.statusText,
      };
      try {
        error.response.data = await response.json();
      } catch {
        // error body is not JSON (e.g. HTML, plain text, or empty) — leave data undefined
      }
      throw error;
    }

    const isEmpty =
      response.status === 204 ||
      response.headers.get('content-length') === '0' ||
      !response.headers.get('content-type')?.includes('application/json');

    if (isEmpty) {
      return { data: null };
    }

    const data = await response.json();
    return { data };
  };

  return {
    get: (path, config) =>
      request(path, { method: 'GET', signal: config?.signal }),
    post: (path, data) => request(path, { method: 'POST', body: data }),
    put: (path, data) => request(path, { method: 'PUT', body: data }),
    delete: (path, config) =>
      request(path, { method: 'DELETE', body: config?.data }),
  };
};

export default createApiClient;
